import type {
  FeedbackCreatedResponse,
  FeedbackDraft,
  FeedbackSubmission,
  FeedbackSubmitResult,
} from "@afar-research/contracts";

import {
  describeFeedbackFailure,
  screenshotRefusalFieldErrors,
  validateFeedbackDraft,
  type ValidFeedbackDraft,
} from "./feedback.logic.ts";

/**
 * Turning a Feedback draft into a Feedback Issue, shared by every client that can reach Connect.
 *
 * Desktop answers its IPC call with `submitFeedbackDraft` and the Runtime Web Client calls it
 * directly; each only supplies the account-authenticated `POST /v1/feedback` and what it can say
 * about its own version and platform (ADR-0105).
 */
export async function submitFeedbackDraft(input: {
  readonly draft: FeedbackDraft;
  readonly clientVersion: string;
  readonly platform: string;
  readonly submit: (submission: FeedbackSubmission) => Promise<FeedbackCreatedResponse>;
}): Promise<FeedbackSubmitResult> {
  const validated = validateFeedbackDraft(input.draft);
  if (!validated.valid) return { status: "invalid", fieldErrors: validated.errors };
  try {
    const created = await input.submit({
      ...validated.value,
      clientVersion: input.clientVersion,
      platform: input.platform,
      idempotencyKey: await idempotencyKeyFor(validated.value),
    });
    // A Connect that predates Feedback Screenshots reports no counts because it attached none.
    const attached = created.screenshotsAttached ?? 0;
    return {
      status: "created",
      reference: created.reference,
      screenshotsAttached: attached,
      screenshotsDropped:
        created.screenshotsDropped ?? (validated.value.screenshots?.length ?? 0) - attached,
    };
  } catch (error) {
    const fieldErrors = screenshotRefusalFieldErrors(error);
    if (fieldErrors) return { status: "invalid", fieldErrors };
    return { status: "failed", ...describeFeedbackFailure(error) };
  }
}

/**
 * Derived from the draft's own content, so a user who resends an unchanged report after a lost
 * response is answered with the original reference rather than filing it twice. A random key would
 * defeat Connect's idempotency row for exactly the retry it exists to absorb.
 *
 * Each screenshot contributes its digest, so a report resent with a different screenshot is a new
 * report. A report without screenshots keys exactly as it did before they existed.
 */
async function idempotencyKeyFor(draft: ValidFeedbackDraft): Promise<string> {
  const screenshotDigests = await Promise.all(
    (draft.screenshots ?? []).map((screenshot) => sha256Hex(screenshot.data)),
  );
  return sha256Hex(
    JSON.stringify([draft.category, draft.summary, draft.details, ...screenshotDigests]),
  );
}

async function sha256Hex(text: string): Promise<string> {
  const content = new TextEncoder().encode(text);
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", content));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
